import { supabase } from './supabaseClient';

const DEFAULT_PAGE = 5;   // unfiltered: 5 most recent
const FILTERED_PAGE = 10; // any filter set: 10 per page

function hasAnyFilter(f) {
  return !!(f.name || f.teamLead || f.countryId || f.dateFrom || f.dateTo || f.kpiSystemId || f.piSystemId || f.completed != null);
}

export const dashboardService = {
  async kpiSummary(f = {}) {
    const { data, error } = await supabase.rpc('dashboard_kpi_summary', {
      p_name: f.name || null,
      p_supervisor: f.teamLead || null,
      p_country_id: f.countryId || null,
      p_date_from: f.dateFrom || null,
      p_date_to: f.dateTo || null,
      p_kpi_system_id: f.kpiSystemId || null,
      p_completed: f.completed == null ? null : f.completed,
    });
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  },

  // RLS (submissions_select_reviewer + kpis_select_reviewer) scopes the visible
  // rows; this only adds approved+kpi + filters + pagination.
  async listApprovedKpis(f = {}, page = 0) {
    const size = hasAnyFilter(f) ? FILTERED_PAGE : DEFAULT_PAGE;
    let q = supabase
      .from('submissions')
      .select('*, kpi_systems(name), reviewer:users!submissions_reviewed_by_fkey(id, name), submitter:users!submissions_user_id_fkey(name, country_id, countries(name), team_lead:users!team_lead_id(id, name)), kpis(kpi_definition_id, metric_name, target_value, current_value)', { count: 'exact' })
      .eq('status', 'approved')
      .eq('type', 'kpi');
    if (f.kpiSystemId) q = q.eq('kpi_system_id', f.kpiSystemId);
    if (f.dateFrom) q = q.gte('period_end', f.dateFrom);
    if (f.dateTo) q = q.lte('period_start', f.dateTo);
    q = q.order('reviewed_at', { ascending: false }).order('id', { ascending: false }).range(page * size, page * size + size - 1);
    const { data, count, error } = await q;
    if (error) throw error;
    return { rows: data, count: count ?? (data ? data.length : 0) };
  },

  async performanceSummary(f = {}) {
    const { data, error } = await supabase.rpc('dashboard_performance_summary', {
      p_name: f.name || null,
      p_supervisor: f.teamLead || null,
      p_country_id: f.countryId || null,
      p_date_from: f.dateFrom || null,
      p_date_to: f.dateTo || null,
      p_pi_system_id: f.piSystemId || null,
    });
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  },

  // RLS (submissions_select_reviewer + performance_data reviewer policy) scopes the
  // visible rows; this only adds approved+performance + filters + pagination.
  async listApprovedPerformance(f = {}, page = 0) {
    const size = hasAnyFilter(f) ? FILTERED_PAGE : DEFAULT_PAGE;
    let q = supabase
      .from('submissions')
      .select('*, pi_systems(name), reviewer:users!submissions_reviewed_by_fkey(id, name), submitter:users!submissions_user_id_fkey(name, country_id, countries(name), team_lead:users!team_lead_id(id, name)), performance_data(pi_parameter_id, value), pi_rewards(amount, qualified, breakdown)', { count: 'exact' })
      .eq('status', 'approved')
      .eq('type', 'performance');
    if (f.piSystemId) q = q.eq('pi_system_id', f.piSystemId);
    if (f.dateFrom) q = q.gte('period_end', f.dateFrom);
    if (f.dateTo) q = q.lte('period_start', f.dateTo);
    q = q.order('reviewed_at', { ascending: false }).order('id', { ascending: false }).range(page * size, page * size + size - 1);
    const { data, count, error } = await q;
    if (error) throw error;
    return { rows: data, count: count ?? (data ? data.length : 0) };
  },

  async reportsSummary(f = {}) {
    const { data, error } = await supabase.rpc('dashboard_reports_summary', {
      p_name: f.name || null,
      p_supervisor: f.teamLead || null,
      p_country_id: f.countryId || null,
      p_date_from: f.dateFrom || null,
      p_date_to: f.dateTo || null,
    });
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
  },

  // RLS (submissions_select_reviewer + report_content reviewer policy) scopes the
  // visible rows; this only adds approved+report + filters + pagination.
  async listApprovedReports(f = {}, page = 0) {
    const size = hasAnyFilter(f) ? FILTERED_PAGE : DEFAULT_PAGE;
    let q = supabase
      .from('submissions')
      .select('*, report_systems(name), countries(name), reviewer:users!submissions_reviewed_by_fkey(id, name), submitter:users!submissions_user_id_fkey(name, email, country_id, countries(name), team_lead:users!team_lead_id(id, name)), report_content(report_field_id, value)', { count: 'exact' })
      .eq('status', 'approved')
      .eq('type', 'report');
    if (f.dateFrom) q = q.gte('period_end', f.dateFrom);
    if (f.dateTo) q = q.lte('period_start', f.dateTo);
    q = q.order('reviewed_at', { ascending: false }).order('id', { ascending: false }).range(page * size, page * size + size - 1);
    const { data, count, error } = await q;
    if (error) throw error;
    return { rows: data, count: count ?? (data ? data.length : 0) };
  },

  // Like listApprovedReports but returns ALL matching rows (no pagination) for export.
  async listAllApprovedReportsForExport(f = {}) {
    let q = supabase
      .from('submissions')
      .select('*, report_systems(name), countries(name), reviewer:users!submissions_reviewed_by_fkey(id, name), submitter:users!submissions_user_id_fkey(name, email, country_id, countries(name), team_lead:users!team_lead_id(id, name)), report_content(report_field_id, value)')
      .eq('status', 'approved')
      .eq('type', 'report');
    if (f.dateFrom) q = q.gte('period_end', f.dateFrom);
    if (f.dateTo) q = q.lte('period_start', f.dateTo);
    q = q.order('reviewed_at', { ascending: false }).order('id', { ascending: false });
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },
};
